import { useMemo } from 'react'
import { Link, useParams } from 'react-router'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useUsers, useUserMutations } from '@klear/ui/hooks/use-user'
import { useEmailMutations } from '@klear/ui/hooks/use-email'
import { useKlearAgGridTheme } from '@klear/ui/hooks/use-ag-grid-theme'
import { DataTableRowActions } from '@klear/ui/components/data-table-row-actions'
import { DropdownMenuItem } from '@klear/ui/components/dropdown-menu'
import { DeleteButton } from '@klear/ui/components/delete-button'
import LoadingSpinner from '@klear/ui/components/spinner'
import type { User } from '@klear/user/user'
import {
  PERMISSION_ENTITY_USER,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_WELCOME_EMAIL,
  TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS,
  ROLE_LABELS
} from '@klear/constants'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

function canManageUsers(user: ReturnType<typeof useAuth>['user']): boolean {
  if (user.hasKlearRole) return true
  return user.roles?.some((r) => TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS.includes(r.role)) ?? false
}

function TeamRowActions({
  userId,
  traderId,
  hasLoggedIn,
  rowUser
}: {
  userId: number
  traderId: number
  hasLoggedIn: boolean
  rowUser: User
}) {
  const { user } = useAuth()
  const { removeFromOrg, isRemovingFromOrg } = useUserMutations()
  const { sendWelcomeEmail, isSendingWelcomeEmail } = useEmailMutations()
  const canManage = canManageUsers(user)
  const orgOwnerRowLocked = rowUser.hasOrganizationOwnerRoleForTrader(traderId) && !user.hasKlearRole
  const showRowActions = canManage && !orgOwnerRowLocked

  return (
    <DataTableRowActions>
      {showRowActions && user?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_EDIT) && (
        <Link to={`${userId}/edit`}>
          <DropdownMenuItem>Edit</DropdownMenuItem>
        </Link>
      )}
      {showRowActions &&
        !hasLoggedIn &&
        user?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_WELCOME_EMAIL) && (
          <DropdownMenuItem disabled={isSendingWelcomeEmail} onSelect={() => sendWelcomeEmail(userId)}>
            {isSendingWelcomeEmail ? 'Sending...' : 'Resend Welcome Email'}
          </DropdownMenuItem>
        )}
      {showRowActions && user?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_DELETE) && (
        <DeleteButton
          wrapperComponent={DropdownMenuItem}
          resourceId={userId}
          resourceName="user"
          resourceType="user"
          deleteMutation={() => removeFromOrg({ userId, traderId })}
          isDeleting={isRemovingFromOrg}
        />
      )}
    </DataTableRowActions>
  )
}

export default function TeamSettings() {
  useDocumentTitle('Team')
  const { user } = useAuth()
  const { traderId } = useParams()
  const numericTraderId = parseInt(traderId!)
  const { users, isLoading } = useUsers(numericTraderId)
  const agGridTheme = useKlearAgGridTheme()

  const canManage = canManageUsers(user)
  const canAdd = canManage

  const columnDefs = useMemo<ColDef<User>[]>(
    () => [
      {
        headerName: 'Name',
        valueGetter: (params) => params.data?.fullName ?? '',
        flex: 1,
        minWidth: 150
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
        minWidth: 200
      },
      {
        headerName: 'Role',
        valueGetter: (params) => {
          const role = params.data?.roles?.[0]?.role
          return role ? (ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role) : '—'
        },
        flex: 1,
        minWidth: 130
      },
      ...(canManage
        ? [
            {
              headerName: '',
              cellRenderer: (params: ICellRendererParams<User>) => {
                if (params.data?.id == null) return null
                return (
                  <TeamRowActions
                    userId={params.data.id}
                    traderId={numericTraderId}
                    hasLoggedIn={params.data.lastLoginTime != null}
                    rowUser={params.data}
                  />
                )
              },
              width: 60,
              pinned: 'right' as const,
              sortable: false,
              filter: false,
              resizable: false
            }
          ]
        : [])
    ],
    [canManage, numericTraderId]
  )

  if (!user.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_VIEW)) {
    return null
  }

  return (
    <div className="flex flex-col p-6 gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Team</h1>
        {canAdd && (
          <Link
            to="new"
            className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
          >
            Add User
          </Link>
        )}
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="w-full h-full min-h-[600px]">
          <AgGridReact<User>
            rowData={users ?? []}
            columnDefs={columnDefs}
            defaultColDef={{ sortable: true, resizable: true, flex: 1, minWidth: 100 }}
            theme={agGridTheme}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
          />
        </div>
      )}
    </div>
  )
}
